export interface LoginDto {
  auth: string;
  user: UserToken;
  blob?: BlobStorageAccess;
  appInsights?: ApplicationInsights;
}

/**
 * Features of platform. Not all platforms support all integration features. Some features need API-Token which need to be setup.
 * Values of feautes:
 *   `undefined`: feature not available for the platform.
 *   `false`: it's available but not enabled due to missing permission.
 *   `true`: enabled, you can use it.
 */
export interface PlatformFeatures {
  multilang?: boolean;
  notification?: boolean;
  userpicker?: boolean;
}

export enum PlatformPermission {
  accelerometer = 'accelerometer',
  autoplay = 'autoplay',
  camera = 'camera',
  clipboardRead = 'clipboard-read',
  clipboardWrite = 'clipboard-write',
  displayCapture = 'display-capture',
  fullscreen = 'fullscreen',
  geolocation = 'geolocation',
  gyroscope = 'gyroscope',
  magnetometer = 'magnetometer',
  microphone = 'microphone',
  pictureInPicture = 'picture-in-picture',
  screenWakeLock = 'screen-wake-lock',
  usb = 'usb',
}

export interface PlatformDetails {
  name: string; // 'staffbase', 'coyo', ...
  features: PlatformFeatures;
  permissions: PlatformPermission[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sdk: { config: { [key: string]: any }; v1: string };
}

export enum Override {
  i18n = 'i18n',
}

export type UserRole = 'user' | 'editor';

export interface MapperProvidedUserToken {
  instance_id: string; // plugin instance id
  instance_name: string; // plugin instance name
  branch_id: string; // customer id
  branch_slug: string; // customer slug
  sub: string; // user id
  name: string; // user name
  role: UserRole;
  locale: string; // favorite content language, f.e. 'en-US'
  theming_bg?: string; // corporate background color, f.e. '#000000'
}

export type TokenExpiration = Record<'exp' | 'nbf' | 'iat', number>;
export interface IngressTokenPayload
  extends MapperProvidedUserToken,
    TokenExpiration {
  aud: string;
  iss: string;
  permissions: PlatformPermission[];
  external_id: string;
  type: 'user';
  theming_text: string;
  features: PlatformFeatures;
}

export interface UserToken extends MapperProvidedUserToken {
  platform: PlatformDetails;
  aud: string; // plugin name
  license: string; // middleware license id
  overrides: Override[];
  type: 'user';
}

export interface BlobStorageAccess {
  url: string;
  sas: string;
}

export interface ApplicationInsights {
  instrumentationKey: string;
}

export interface MapperProvidedApiToken {
  /** Mapped as `external_id` in the middleware instance table.
   * The instance's primary `id` key which is also used in the target plugins
   *  is generated separately in the associated `InstanceService`'s
   * `mapAndTrackInstanceId` method. */
  instance_id: string;
  tenant_id: string;
  sub?: string;
}

export interface ApiToken extends MapperProvidedApiToken {
  aud: string;
  license: string;
  type: 'token';
}
